/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * Deepgram speech-to-text and voice (TTS) models offered in the voice
 * conversation settings. The picked ids are sent in `session.start.metadata`
 * (`sttModel`, `ttsVoice`); the server only checks their shape, so this list
 * is the catalog.
 */
export const VOICE_STT_MODELS = [
  {
    model: 'flux-general-en',
    languages: ['en', 'en-AU', 'en-CA', 'en-GB', 'en-IN', 'en-NZ', 'en-US'],
  },
  {
    model: 'flux-general-multi',
    languages: ['de', 'en', 'en-AU', 'en-CA', 'en-GB', 'en-IN', 'en-NZ', 'en-US', 'es', 'fr', 'hi', 'it', 'ja', 'nl', 'pt', 'ru'],
  },
];

const FLUX_SAMPLE = 'https://cdn.sanity.io/files/0dsbfl6j/production/';
const AURA_2_SAMPLE = 'https://static.deepgram.com/examples/';
const AURA_SAMPLE = 'https://res.cloudinary.com/deepgram/video/upload/';

const voice = (model, language, gender, sample) => ({ model, language, gender, sample });

export const VOICE_TTS_VOICES = [
  voice('flux-alexis-en', 'en-US', 'Female', `${FLUX_SAMPLE}89f2fa37d4624150a08f8344684e37e5588132ca.wav`),
  voice('flux-bree-en', 'en-US', 'Female', `${FLUX_SAMPLE}a8f87bc7be0fb6695549500b1b9d6e1c5e0e5523.wav`),
  voice('flux-brittany-en', 'en-US', 'Female', `${FLUX_SAMPLE}091777b9d5955221f6af10b9cdf6c5614b64f103.wav`),
  voice('flux-brooke-en', 'en-US', 'Female', `${FLUX_SAMPLE}1074414f7f23143c2500175dea1b64af5065e305.wav`),
  voice('flux-bruce-en', 'en-US', 'Male', `${FLUX_SAMPLE}9bec45c2ecf2c471c17c812fbcd32a91fa810091.wav`),
  voice('flux-cliff-en', 'en-US', 'Male', `${FLUX_SAMPLE}eec9781f5e6d691015223da0574dfddd09dc4f7e.wav`),
  voice('flux-cole-en', 'en-US', 'Male', `${FLUX_SAMPLE}03f08c6a562baede80f483f0a0a6e05bf304285e.wav`),
  voice('flux-donovan-en', 'en-US', 'Male', `${FLUX_SAMPLE}d04fabe593dcdc42d28994aee4ef4af9eff47cb4.wav`),
  voice('flux-drew-en', 'en-US', 'Male', `${FLUX_SAMPLE}a470cb28e426deca29d6580872b79438448efc81.wav`),
  voice('flux-elise-en', 'en-US', 'Female', `${FLUX_SAMPLE}aae6de6487068b214ef56a28a309385b33f17d19.wav`),
  voice('flux-haley-en', 'en-US', 'Female', `${FLUX_SAMPLE}79d681acbf6a05ed02d1177d16d6157687fc8295.wav`),
  voice('flux-hannah-en', 'en-US', 'Female', `${FLUX_SAMPLE}0d0c29951eb6f7de08b40844609143f62983d7f8.wav`),
  voice('flux-heather-en', 'en-US', 'Female', `${FLUX_SAMPLE}f33180630767024586cb5ed933b6893fb38af9d9.wav`),
  voice('flux-kelsey-en', 'en-US', 'Female', `${FLUX_SAMPLE}723dde97c1e3faf80eb557a23027536e44640df2.wav`),
  voice('flux-marcus-en', 'en-US', 'Male', `${FLUX_SAMPLE}8ec4362caf78524acf72c63705924fd27d24de76.wav`),
  voice('flux-meghan-en', 'en-US', 'Female', `${FLUX_SAMPLE}5d76d99f4d0f7338350e2646108cd26833fe9f57.wav`),
  voice('flux-miles-en', 'en-US', 'Male', `${FLUX_SAMPLE}f7bbc7f67bddaa466df322d0c7d802e84441fef1.wav`),
  voice('flux-paige-en', 'en-US', 'Female', `${FLUX_SAMPLE}e588c071429cb1c59778d14d3b8225abaa033e76.wav`),
  voice('flux-sienna-en', 'en-US', 'Female', `${FLUX_SAMPLE}41c9c4cfda31d9a4430cbe57b4d11bb6273fbb37.wav`),
  voice('flux-wade-en', 'en-US', 'Male', `${FLUX_SAMPLE}3350409b3121803d11b08e1ecbb728591f93fc40.wav`),
  voice('flux-wes-en', 'en-US', 'Male', `${FLUX_SAMPLE}326a0b90d64f36ede725ff730c6afdf387860bd8.wav`),
  voice('aura-2-andromeda-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-andromeda.wav`),
  voice('aura-2-apollo-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-apollo.wav`),
  voice('aura-2-arcas-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-arcas.wav`),
  voice('aura-2-aries-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-aries.wav`),
  voice('aura-2-asteria-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-asteria.wav`),
  voice('aura-2-athena-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-athena.wav`),
  voice('aura-2-atlas-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-atlas.wav`),
  voice('aura-2-aurora-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-aurora.wav`),
  voice('aura-2-callista-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-callista.wav`),
  voice('aura-2-cora-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-cora.wav`),
  voice('aura-2-cordelia-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-cordelia.wav`),
  voice('aura-2-delia-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-delia.wav`),
  voice('aura-2-electra-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-electra.wav`),
  voice('aura-2-harmonia-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-harmonia.wav`),
  voice('aura-2-helena-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-helena.wav`),
  voice('aura-2-hera-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-hera.wav`),
  voice('aura-2-hermes-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-hermes.wav`),
  voice('aura-2-iris-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-iris.wav`),
  voice('aura-2-janus-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-janus.wav`),
  voice('aura-2-juno-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-juno.wav`),
  voice('aura-2-jupiter-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-jupiter.wav`),
  voice('aura-2-luna-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-luna.wav`),
  voice('aura-2-mars-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-mars.wav`),
  voice('aura-2-minerva-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-minerva.wav`),
  voice('aura-2-neptune-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-neptune.wav`),
  voice('aura-2-odysseus-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-odysseus.wav`),
  voice('aura-2-ophelia-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-ophelia.wav`),
  voice('aura-2-orion-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-orion.wav`),
  voice('aura-2-orpheus-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-orpheus.wav`),
  voice('aura-2-phoebe-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-phoebe.wav`),
  voice('aura-2-pluto-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-pluto.wav`),
  voice('aura-2-saturn-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-saturn.wav`),
  voice('aura-2-selene-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-selene.wav`),
  voice('aura-2-thalia-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-thalia.wav`),
  voice('aura-2-vesta-en', 'en-US', 'Female', `${AURA_2_SAMPLE}Aura-2-vesta.wav`),
  voice('aura-2-zeus-en', 'en-US', 'Male', `${AURA_2_SAMPLE}Aura-2-zeus.wav`),
  voice('aura-asteria-en', 'en-US', 'Female', `${AURA_SAMPLE}v1709565353/aura/asteria_docs_venw0r.wav`),
  voice('aura-luna-en', 'en-US', 'Female', `${AURA_SAMPLE}v1709565351/aura/luna_docs_clom0e.wav`),
  voice('aura-stella-en', 'en-US', 'Female', `${AURA_SAMPLE}v1709565349/aura/stella_docs_xh5jbv.wav`),
  voice('aura-hera-en', 'en-US', 'Female', `${AURA_SAMPLE}v1709565347/aura/hera_docs_xjkt4x.wav`),
  voice('aura-orion-en', 'en-US', 'Male', `${AURA_SAMPLE}v1709565346/aura/orion_docs_aljv1q.mp3`),
  voice('aura-arcas-en', 'en-US', 'Male', `${AURA_SAMPLE}v1709565348/aura/arcas_docs_pc9hxp.mp3`),
  voice('aura-perseus-en', 'en-US', 'Male', `${AURA_SAMPLE}v1709565350/aura/perseus_docs_ap7fc0.wav`),
  voice('aura-orpheus-en', 'en-US', 'Male', `${AURA_SAMPLE}v1709565350/aura/orpheus_docs_zdlpcf.wav`),
  voice('aura-zeus-en', 'en-US', 'Male', `${AURA_SAMPLE}v1709565347/aura/zeus_docs_fupdiv.wav`),
  voice('aura-2-estrella-es', 'es-MX', 'Female', `${AURA_2_SAMPLE}Estrella.wav`),
  voice('aura-2-olivia-es', 'es-MX', 'Female', `${AURA_2_SAMPLE}Spanish_speaker_750_Olivia.wav`),
  voice('aura-2-sirio-es', 'es-MX', 'Male', `${AURA_2_SAMPLE}Sirio.wav`),
  voice('aura-2-javier-es', 'es-MX', 'Male', `${AURA_2_SAMPLE}Javier.wav`),
  voice('aura-2-luciano-es', 'es-MX', 'Male', `${AURA_2_SAMPLE}Spanish_speaker_695_Luciano.wav`),
  voice('aura-2-valerio-es', 'es-MX', 'Male', `${AURA_2_SAMPLE}Spanish_speaker_743_Valerio.wav`),
];

/** Pre-selected until the user picks something else. */
export const DEFAULT_STT_MODEL = 'flux-general-en';
export const DEFAULT_TTS_VOICE = 'aura-2-thalia-en';

const STT_LABELS = {
  'flux-general-en': 'Flux · English',
  'flux-general-multi': 'Flux · Multilingual',
};

export const findSttModel = (model) => VOICE_STT_MODELS.find((m) => m.model === model);
export const findTtsVoice = (model) => VOICE_TTS_VOICES.find((v) => v.model === model);

export const sttModelLabel = (model) => STT_LABELS[model] || model;

/** "Flux", "Aura 2" or "Aura" — the Deepgram voice family. */
export const ttsFamily = (model) => {
  if (model.startsWith('flux-')) return 'Flux';
  if (model.startsWith('aura-2-')) return 'Aura 2';
  return 'Aura';
};

/** "aura-2-thalia-en" → "Thalia". */
export const ttsVoiceName = (model) => {
  const name = model.replace(/^(flux|aura-2|aura)-/, '').replace(/-[a-z]{2}$/, '');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

let displayNames;
try {
  displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
} catch { /* older browsers: codes are shown as-is */ }

/** "en-GB" → "British English", "es-MX" → "Mexican Spanish" (falls back to the code). */
export const languageName = (code) => {
  try {
    return displayNames?.of(code) || code;
  } catch {
    return code;
  }
};

/** Primary language subtag: "en-US" → "en". */
export const baseLanguage = (code) => (code || '').split('-')[0].toLowerCase();

/** Voices grouped for `<optgroup>`s: "Aura 2 · American English", in catalog order. */
export const TTS_VOICE_GROUPS = VOICE_TTS_VOICES.reduce((groups, v) => {
  const label = `${ttsFamily(v.model)} · ${languageName(v.language)}`;
  const group = groups.find((g) => g.label === label);
  if (group) group.voices.push(v);
  else groups.push({ label, voices: [v] });
  return groups;
}, []);
